(function () {
  if (window.NESWWishlist) return;

  var LOGGED_IN = !!window.NESW_WISHLIST_LOGGED_IN;
  var LOGIN_URL = window.NESW_WISHLIST_LOGIN_URL || '/account/login';
  var API_BASE = '/apps/ignis/wishlist';

  /* In-memory cache. Mirrors what's on the server for the logged-in
     customer. Guests have an empty cache and can never write to it. */
  var cache = [];
  var seededOnce = false;
  var seedInFlight = null;

  function keyOf(item) {
    if (!item || !item.handle) return '';
    if (item.variantId) return item.handle + ':' + item.variantId;
    if (item.color) return item.handle + '::' + item.color;
    return item.handle + ':';
  }

  function read() {
    /* Always return a fresh array so callers can't mutate the cache. */
    return cache.slice();
  }

  function has(itemOrHandle) {
    var item = typeof itemOrHandle === 'string' ? { handle: itemOrHandle } : (itemOrHandle || {});
    var k = keyOf(item);
    if (!k) return false;
    return cache.some(function (x) { return keyOf(x) === k; });
  }

  /* ─── Sign-in prompt for guests ────────────────────────────────────── */
  var toastEl = null;
  var toastTimer = null;
  function showToast(opts) {
    opts = opts || {};
    if (toastTimer) { clearTimeout(toastTimer); toastTimer = null; }
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'nesw-wishlist-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.classList.toggle('nesw-wishlist-toast--error', !!opts.isError);
    while (toastEl.firstChild) toastEl.removeChild(toastEl.firstChild);

    var msg = document.createElement('span');
    msg.className = 'nesw-wishlist-toast__msg';
    msg.textContent = opts.message || '';
    toastEl.appendChild(msg);

    if (opts.ctaLabel && opts.ctaHref) {
      var cta = document.createElement('a');
      cta.className = 'nesw-wishlist-toast__cta';
      cta.href = opts.ctaHref;
      cta.textContent = opts.ctaLabel;
      toastEl.appendChild(cta);
    }

    /* Defer the class flip to next frame so the transition runs. */
    requestAnimationFrame(function () { toastEl.classList.add('is-open'); });
    toastTimer = setTimeout(function () {
      toastEl.classList.remove('is-open');
    }, opts.duration || 3600);
  }

  function promptSignIn() {
    var path = window.location.pathname + window.location.search;
    showToast({
      message: 'Sign in to save items.',
      ctaLabel: 'Sign in',
      ctaHref: LOGIN_URL + '?return_url=' + encodeURIComponent(path),
      duration: 4200
    });
  }

  /* ─── Server I/O via App Proxy ─────────────────────────────────────── */
  function apiGet() {
    return fetch(API_BASE, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    }).then(function (r) {
      if (!r.ok) throw new Error('GET ' + r.status);
      return r.json();
    });
  }
  function apiPost(item) {
    return fetch(API_BASE, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ item: item })
    }).then(function (r) {
      if (!r.ok) throw new Error('POST ' + r.status);
      return r.json();
    });
  }
  function apiDelete(item) {
    return fetch(API_BASE, {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        handle: item.handle,
        variantId: item.variantId || '',
        color: item.color || ''
      })
    }).then(function (r) {
      if (!r.ok) throw new Error('DELETE ' + r.status);
      return r.json();
    });
  }

  function seed() {
    if (!LOGGED_IN) return Promise.resolve([]);
    if (seededOnce) return Promise.resolve(cache.slice());
    if (seedInFlight) return seedInFlight;
    seedInFlight = apiGet()
      .then(function (data) {
        cache = Array.isArray(data && data.items) ? data.items : [];
        seededOnce = true;
        dispatch();
        return cache.slice();
      })
      .catch(function () {
        /* Network error or 401 (e.g. App Proxy misconfigured) — keep
           cache empty and let buttons render in the un-saved state.
           Don't block the page. */
        seededOnce = true;
        return [];
      })
      .then(function (res) { seedInFlight = null; return res; });
    return seedInFlight;
  }

  /* ─── Mutations ────────────────────────────────────────────────────── */
  function add(item) {
    if (!item || !item.handle) return Promise.resolve(false);
    if (!LOGGED_IN) { promptSignIn(); return Promise.resolve(false); }
    var k = keyOf(item);
    if (cache.some(function (x) { return keyOf(x) === k; })) {
      return Promise.resolve(true);
    }
    /* Optimistic UI: paint as saved, revert on server error. */
    var optimisticEntry = {
      handle: item.handle,
      variantId: item.variantId || '',
      size: item.size || '',
      color: item.color || '',
      title: item.title || item.handle,
      image: item.image || '',
      price: item.price || '',
      url: item.url || ('/products/' + item.handle + (item.variantId ? '?variant=' + item.variantId : '')),
      saved_at: Date.now()
    };
    cache.push(optimisticEntry);
    dispatch();
    return apiPost(optimisticEntry)
      .then(function (data) {
        if (data && Array.isArray(data.items)) {
          cache = data.items;
          dispatch();
        }
        /* Analytics: wishlist saves are a high-intent signal. Fire only after
           the server persist succeeds so counts stay honest. */
        try {
          if (window.pxl && window.pxl.track) {
            window.pxl.track('wishlist_add', {
              handle: optimisticEntry.handle,
              variant_id: optimisticEntry.variantId || '',
              title: optimisticEntry.title || '',
              size: optimisticEntry.size || '',
              color: optimisticEntry.color || '',
              price: optimisticEntry.price || ''
            });
          }
        } catch (e) { /* analytics must never break the wishlist */ }
        return true;
      })
      .catch(function (err) {
        /* Revert optimistic add. */
        cache = cache.filter(function (x) { return keyOf(x) !== k; });
        dispatch();
        var is429 = err && /\b429\b/.test(String(err.message));
        showToast({
          message: is429 ? 'Slow down a sec.' : 'Could not save. Try again.',
          isError: true,
          duration: 2400
        });
        return false;
      });
  }

  function remove(itemOrHandle) {
    if (!LOGGED_IN) return Promise.resolve(false);
    var target;
    if (typeof itemOrHandle === 'string') {
      target = { handle: itemOrHandle };
    } else {
      target = itemOrHandle || {};
    }
    var k = keyOf(target);
    var prev = cache.slice();
    if (typeof itemOrHandle === 'string') {
      /* Legacy: remove all entries matching this handle. */
      cache = cache.filter(function (x) { return x.handle !== itemOrHandle; });
    } else {
      cache = cache.filter(function (x) { return keyOf(x) !== k; });
    }
    dispatch();
    return apiDelete(target)
      .then(function (data) {
        if (data && Array.isArray(data.items)) {
          cache = data.items;
          dispatch();
        }
        try {
          if (window.pxl && window.pxl.track) {
            window.pxl.track('wishlist_remove', {
              handle: target.handle || '',
              variant_id: target.variantId || ''
            });
          }
        } catch (e) { /* analytics must never break the wishlist */ }
        return true;
      })
      .catch(function (err) {
        cache = prev;
        dispatch();
        var is429 = err && /\b429\b/.test(String(err.message));
        showToast({
          message: is429 ? 'Slow down a sec.' : 'Could not update. Try again.',
          isError: true,
          duration: 2400
        });
        return false;
      });
  }

  function clear() {
    if (!LOGGED_IN) return Promise.resolve(false);
    /* No bulk-clear endpoint — issue parallel deletes for everything. */
    var snapshot = cache.slice();
    cache = [];
    dispatch();
    return Promise.all(snapshot.map(function (item) {
      return apiDelete(item).catch(function () { return null; });
    })).then(function () { return true; });
  }

  function toggle(item) {
    if (!item || !item.handle) return Promise.resolve(false);
    if (!LOGGED_IN) { promptSignIn(); return Promise.resolve(false); }
    if (has(item)) {
      remove(item);
      return Promise.resolve(false);
    }
    add(item);
    return Promise.resolve(true);
  }

  function dispatch() {
    try {
      window.dispatchEvent(new CustomEvent('nesw:wishlist-change', {
        detail: { count: cache.length }
      }));
    } catch (_) {}
  }

  /* ─── DOM sync (button states + count badge) ───────────────────────── */
  var syncQueued = false;
  function syncButtons() {
    if (syncQueued) return;
    syncQueued = true;
    (window.requestAnimationFrame || function (cb) { return setTimeout(cb, 16); })(function () {
      syncQueued = false;
      var lookup = Object.create(null);
      cache.forEach(function (x) { lookup[keyOf(x)] = true; });
      document.querySelectorAll('.nesw-bookmark').forEach(function (b) {
        var h = b.getAttribute('data-bookmark-handle') || '';
        var v = b.getAttribute('data-bookmark-variant-id') || '';
        var c = b.getAttribute('data-bookmark-color') || '';
        var k = keyOf({ handle: h, variantId: v, color: c });
        var on = !!lookup[k];
        b.classList.toggle('is-saved', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
        el.textContent = cache.length;
        el.setAttribute('data-count', String(cache.length));
      });
    });
  }

  /* ─── Seed card bookmarks from their active swatch (variant-loss fix) ──
     Card bookmarks (home rows, collection page, "Pair It With") are
     COLOR-keyed: the swatch-click handlers set data-bookmark-color +
     clear data-bookmark-variant-id, so a saved card item keys as
     `handle::color`. But the cards render with an EMPTY color until a
     swatch is clicked, so bookmarking the DEFAULT color saved it as
     `handle:` (no color). Switching colors and back then keyed it as
     `handle::color`, which didn't match — the button showed un-saved even
     though it was saved, and re-clicking created a duplicate entry.

     Fix: on load, seed each card bookmark's color from its active (default)
     swatch so the default-state key matches exactly what the click handlers
     produce. PDP bookmarks are variant-id keyed and seeded by the PDP
     script, so they're left alone. Single-color cards (no swatches) keep
     their rendered state. */
  function seedCardBookmarkColors() {
    document.querySelectorAll('.nesw-bookmark').forEach(function (b) {
      if (b.classList.contains('nesw-bookmark--pdp')) return;
      /* Scope = the card wrapper that holds BOTH the bookmark and the
         swatches (NOT the inner <a>, which excludes the sibling swatches). */
      var scope = b.closest('.collection-item, .nesw-row__card-wrap, .product-recommendations-card');
      if (!scope) return;
      var swatch = scope.querySelector('.color-swatch.active, .related-color-swatch.active')
                || scope.querySelector('.color-swatch, .related-color-swatch');
      if (!swatch) return; /* single-color product — leave rendered state */
      var color = swatch.getAttribute('data-color') || (swatch.dataset && swatch.dataset.color) || '';
      if (!color) return;
      b.setAttribute('data-bookmark-color', color);
      b.setAttribute('data-bookmark-variant-id', '');
      var img = (swatch.dataset && swatch.dataset.variantImage) || swatch.getAttribute('data-variant-image') || '';
      if (img) b.setAttribute('data-bookmark-image', img);
      var h = b.getAttribute('data-bookmark-handle') || '';
      if (h) b.setAttribute('data-bookmark-url', '/products/' + h + '?color=' + encodeURIComponent(color));
    });
  }

  /* Delegated toggle click. */
  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('.nesw-bookmark');
    if (!b) return;
    e.preventDefault();
    e.stopPropagation();
    toggle({
      handle: b.getAttribute('data-bookmark-handle'),
      variantId: b.getAttribute('data-bookmark-variant-id') || '',
      size: b.getAttribute('data-bookmark-size') || '',
      color: b.getAttribute('data-bookmark-color') || '',
      title: b.getAttribute('data-bookmark-title'),
      image: b.getAttribute('data-bookmark-image'),
      price: b.getAttribute('data-bookmark-price'),
      url: b.getAttribute('data-bookmark-url')
    });
  });

  window.addEventListener('nesw:wishlist-change', syncButtons);
  function initBookmarks() { seedCardBookmarkColors(); syncButtons(); }
  document.addEventListener('DOMContentLoaded', initBookmarks);
  if (document.readyState !== 'loading') initBookmarks();

  /* Public API — promise-returning so the wishlist page can await server
     reconciles when it needs them. read()/has() stay synchronous against
     the in-memory cache for snappy UI. */
  window.NESWWishlist = {
    read: read, has: has,
    add: add, remove: remove, clear: clear, toggle: toggle,
    sync: syncButtons, keyOf: keyOf,
    seed: seed,
    /* True once the initial server seed has resolved (success OR failure) so
       the wishlist page can show a loading skeleton until real data is in,
       instead of flashing the empty state while the fetch is in flight. For
       guests there's nothing to fetch, so it's ready immediately. */
    ready: function () { return !LOGGED_IN || seededOnce; },
    loggedIn: function () { return LOGGED_IN; }
  };

  /* Kick off the initial seed for logged-in customers. */
  if (LOGGED_IN) seed();
})();
